/**
 * Sprint 2.7.6 Step 7 — Generic approval modal shell.
 *
 * Used by OutreachDraftModal. Future surfaces (content briefs, fix deployments)
 * should reuse this shell rather than reimplementing modal chrome.
 *
 * Props:
 *   title    — ReactNode so callers can embed badges next to the title text.
 *   isOpen   — controls visibility; caller owns the state.
 *   onClose  — called when overlay or × button is clicked.
 *   children — the main form / body content.
 *   actions  — footer action buttons (optional).
 *   meta     — right-side metadata sidebar (optional; collapses to bottom on mobile).
 */
import type { ReactNode } from 'react';

interface ApprovalModalProps {
  title: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
}

export function ApprovalModal({
  title,
  isOpen,
  onClose,
  children,
  actions,
  meta,
}: ApprovalModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="approval-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div id="approval-modal-title" className="flex items-center gap-2 min-w-0 flex-1 mr-4">
            {title}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* ── Body ─────────────────────────────────────────────────────────── */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {meta ? (
            <div className="flex flex-col lg:flex-row h-full">
              <div className="flex-1 p-6 overflow-y-auto">{children}</div>
              <aside className="lg:w-64 shrink-0 border-t lg:border-t-0 lg:border-l border-gray-100 p-5 bg-gray-50/60 overflow-y-auto">
                {meta}
              </aside>
            </div>
          ) : (
            <div className="p-6">{children}</div>
          )}
        </div>

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        {actions != null && (
          <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap items-center gap-3 shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
